import { z } from 'zod';
import { tagNameSchema } from '../tag/tag';

/** The languages a task may be written or translated in (QZ-D12). */
export const LANGUAGES = ['sv', 'en', 'da', 'nb', 'fi', 'fr', 'es', 'it', 'de'] as const;

/** A task's difficulty (QZ-D14). */
export const DIFFICULTIES = ['easy', 'medium', 'hard'] as const;

/** The age groups a task may be aimed at, youngest first (QZ-D131). */
export const AGE_GROUPS = ['young-children', 'children', 'teens', 'adults'] as const;

/** The task types GPS Quiz persists; the triple "er" of `ItemOrdererer` is its spelling, not a typo (QZ-D16). */
export const TASK_TYPES = [
  'MultiChoiceQuestion',
  'FreetextQuestion',
  'TiebreakerQuestion',
  'NumericTiebreakerQuestion',
  'ItemOrdererer',
] as const;

/** A task's review status (QZ-D37). */
export const TASK_STATUSES = ['draft', 'approved', 'rejected'] as const;

/** A task's language, validated against `LANGUAGES` (QZ-D12). */
export const languageSchema = z.enum(LANGUAGES);
export type Language = z.infer<typeof languageSchema>;

/** A task's difficulty, validated against `DIFFICULTIES` (QZ-D14). */
export const difficultySchema = z.enum(DIFFICULTIES);
export type Difficulty = z.infer<typeof difficultySchema>;

/** A task's age group, validated against `AGE_GROUPS` (QZ-D131). */
export const ageGroupSchema = z.enum(AGE_GROUPS);
export type AgeGroup = z.infer<typeof ageGroupSchema>;

/** The age groups a task lists; one to four, no duplicates (QZ-D131). */
export const ageGroupsSchema = z.array(ageGroupSchema).min(1).max(AGE_GROUPS.length);

/** A task's type, validated against `TASK_TYPES` (QZ-D16). */
export const taskTypeSchema = z.enum(TASK_TYPES);
export type TaskType = z.infer<typeof taskTypeSchema>;

/** A task's review status, validated against `TASK_STATUSES` (QZ-D37). */
export const taskStatusSchema = z.enum(TASK_STATUSES);
export type TaskStatus = z.infer<typeof taskStatusSchema>;

/** The kinds of document a task's source reference may cite (QZ-D93). */
export const SOURCE_KINDS = ['pdf', 'photos'] as const;

/** A source reference's document kind, validated against `SOURCE_KINDS` (QZ-D93). */
export const sourceKindSchema = z.enum(SOURCE_KINDS);
export type SourceKind = z.infer<typeof sourceKindSchema>;

/**
 * The kind of a draft's source reference, from its job's method (QZ-D93). The parameter is the
 * literal union, not `JobMethod`: `job.ts` imports `task.ts`, so the reverse import would be a
 * cycle. Callers narrow with `isMaterialMethod` first.
 */
export const sourceKindOf = (method: 'pdf' | 'photos'): SourceKind => method;

/** A non-empty string after trimming; the stored value is trimmed (QZ-D42). */
export const textSchema = z.string().trim().min(1);

/** A task's id (QZ-D42). */
export const taskIdSchema = z.uuid();

/** A stored timestamp (QZ-D42). */
export const timestampSchema = z.iso.datetime();

/** The task types a task is tagged with; at least one (QZ-D16). */
export const taskTypesSchema = z.array(taskTypeSchema).min(1);

/** A task's tags; one to three (QZ-D15). */
export const tagsSchema = z.array(tagNameSchema).min(1).max(3);

/**
 * The languages a task is in or will be translated into (QZ-D130); always includes the original
 * (T9).
 */
export const targetLanguagesSchema = z.array(languageSchema).min(1).max(LANGUAGES.length);

/** The correct answer to a numeric tiebreaker question (QZ-D42). */
export const numericAnswerSchema = z.number();

/** The explanation shown after a task is answered (QZ-D39). */
export const explanationSchema = textSchema;

/** The most characters a rejection reason may hold (QZ-D156). */
export const REJECTION_REASON_MAX = 500;

/** The reason a task was rejected (QZ-D37). */
export const rejectionReasonSchema = textSchema.max(REJECTION_REASON_MAX);

/** One language's content for a task (QZ-D42). */
export const taskTranslationSchema = z
  .object({
    text: textSchema,
    choices: z.array(textSchema).min(2).max(6).optional(),
    freetextAnswer: textSchema.max(60).optional(),
    items: z.array(textSchema).min(2).max(6).optional(),
  })
  .strip();
export type TaskTranslation = z.infer<typeof taskTranslationSchema>;

/**
 * Where a task's content came from (QZ-D42). `kind` absent means `pdf` (QZ-D42, QZ-D93). A
 * document's optional `section`, named freely (QZ-D101).
 */
export const sourceReferenceSchema = z
  .object({
    url: z.url({ protocol: /^https?$/ }).optional(),
    document: textSchema.optional(),
    page: z.number().int().positive().optional(),
    kind: sourceKindSchema.optional(),
    section: textSchema.max(200).optional(),
  })
  .strip()
  .superRefine((value, ctx) => {
    if (!value.url && !value.document) {
      ctx.addIssue({ code: 'custom', path: ['url'], message: 'Give a URL or a document.' });
    }
    if (value.url && value.document) {
      ctx.addIssue({
        code: 'custom',
        path: ['document'],
        message: 'Give a URL or a document, not both.',
      });
    }
    if (value.page !== undefined && !value.document) {
      ctx.addIssue({ code: 'custom', path: ['page'], message: 'A page number needs a document.' });
    }
    if (value.kind !== undefined && !value.document) {
      ctx.addIssue({ code: 'custom', path: ['kind'], message: 'A kind needs a document.' });
    }
    if (value.section !== undefined && !value.document) {
      ctx.addIssue({
        code: 'custom',
        path: ['section'],
        message: 'A section needs a document.',
      });
    }
  });
export type SourceReference = z.infer<typeof sourceReferenceSchema>;

/** What generation's check found (QZ-D60). */
export const taskCheckSchema = z
  .object({
    outcome: z.enum(['passed', 'revised']),
    note: textSchema.max(500).optional(),
    similarTaskIds: z.array(taskIdSchema).min(1).max(3).optional(),
  })
  .strip();
export type TaskCheck = z.infer<typeof taskCheckSchema>;

/** Where a task's translation stands (QZ-D113). */
export const translationStatusSchema = z.enum(['pending', 'done', 'failed']);
export type TranslationStatus = z.infer<typeof translationStatusSchema>;

/**
 * A task's translation record (QZ-D113) — its status, the attempts made, its cost in
 * micro-dollars over every attempt and every approval, and the last error. Written only by
 * approval and the worker, never by a review edit; absent on a task that has never been
 * approved.
 */
export const taskTranslationStateSchema = z
  .object({
    status: translationStatusSchema,
    attempts: z.number().int().min(0),
    // Spelled out, not microUsdSchema: job.ts imports task.ts, so the reverse import would cycle.
    microUsd: z.number().int().min(0),
    error: z.string().max(500).optional(),
  })
  .strip();
export type TaskTranslationState = z.infer<typeof taskTranslationStateSchema>;

/** A task in the task bank (QZ-D42). Every stored task, drafts included, satisfies this schema. */
export const taskSchema = z
  .object({
    taskId: taskIdSchema,
    status: taskStatusSchema,
    taskTypes: taskTypesSchema,
    difficulty: difficultySchema,
    tags: tagsSchema,
    originalLanguage: languageSchema,
    targetLanguages: targetLanguagesSchema,
    ageGroups: ageGroupsSchema,
    numericAnswer: numericAnswerSchema.optional(),
    /** Absent on a task from an open-web job, which cites no source (QZ-D79). */
    sourceReference: sourceReferenceSchema.optional(),
    explanation: explanationSchema,
    translations: z.partialRecord(languageSchema, taskTranslationSchema),
    rejectionReason: rejectionReasonSchema.optional(),
    jobId: z.string().min(1).optional(),
    check: taskCheckSchema.optional(),
    /** A task's translation record (QZ-D113). */
    translation: taskTranslationStateSchema.optional(),
    /** Times this task has been used by a consumer since approval (QZ-D152). */
    usedCount: z.number().int().min(0).optional(),
    /** Times this task has been declined by a consumer since approval (QZ-D152). */
    declinedCount: z.number().int().min(0).optional(),
    /** One entry per decline, `"<language>: <reason>"` (QZ-D156). */
    declineReasons: z.array(textSchema).optional(),
    /** The time of the last feedback (QZ-D152). */
    feedbackAt: timestampSchema.optional(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  })
  .strip()
  .superRefine((task, ctx) => {
    const hasType = (type: TaskType): boolean => task.taskTypes.includes(type);

    // T1: no duplicate taskTypes.
    if (new Set(task.taskTypes).size !== task.taskTypes.length) {
      ctx.addIssue({
        code: 'custom',
        path: ['taskTypes'],
        message: 'A task type is listed more than once.',
      });
    }

    // T2: no duplicate tags.
    if (new Set(task.tags).size !== task.tags.length) {
      ctx.addIssue({ code: 'custom', path: ['tags'], message: 'A tag is listed more than once.' });
    }

    // T3: the original language has a translation.
    if (!task.translations[task.originalLanguage]) {
      ctx.addIssue({
        code: 'custom',
        path: ['translations', task.originalLanguage],
        message: 'The original language has no translation.',
      });
    }

    for (const [language, translation] of Object.entries(task.translations) as [
      Language,
      TaskTranslation | undefined,
    ][]) {
      if (!translation) {
        continue;
      }

      // T4: choices present exactly when MultiChoiceQuestion is listed.
      if ((translation.choices !== undefined) !== hasType('MultiChoiceQuestion')) {
        ctx.addIssue({
          code: 'custom',
          path: ['translations', language, 'choices'],
          message: 'Choices are needed exactly when the multiple-choice type is listed.',
        });
      }

      // T5: freetextAnswer present exactly when FreetextQuestion is listed.
      if ((translation.freetextAnswer !== undefined) !== hasType('FreetextQuestion')) {
        ctx.addIssue({
          code: 'custom',
          path: ['translations', language, 'freetextAnswer'],
          message: 'A free-text answer is needed exactly when the free-text type is listed.',
        });
      }

      // T6: items present exactly when ItemOrdererer is listed.
      if ((translation.items !== undefined) !== hasType('ItemOrdererer')) {
        ctx.addIssue({
          code: 'custom',
          path: ['translations', language, 'items'],
          message: 'Items are needed exactly when the item-ordering type is listed.',
        });
      }

      // T7: no duplicate choices or items within a translation.
      if (translation.choices && new Set(translation.choices).size !== translation.choices.length) {
        ctx.addIssue({
          code: 'custom',
          path: ['translations', language, 'choices'],
          message: 'A choice is listed more than once.',
        });
      }
      if (translation.items && new Set(translation.items).size !== translation.items.length) {
        ctx.addIssue({
          code: 'custom',
          path: ['translations', language, 'items'],
          message: 'An item is listed more than once.',
        });
      }
    }

    // T8: numericAnswer present exactly when NumericTiebreakerQuestion is listed.
    if ((task.numericAnswer !== undefined) !== hasType('NumericTiebreakerQuestion')) {
      ctx.addIssue({
        code: 'custom',
        path: ['numericAnswer'],
        message: 'A numeric answer is needed exactly when the numeric tiebreaker type is listed.',
      });
    }

    // T9: targetLanguages has no duplicates and covers originalLanguage and every translation.
    if (new Set(task.targetLanguages).size !== task.targetLanguages.length) {
      ctx.addIssue({
        code: 'custom',
        path: ['targetLanguages'],
        message: 'A language is listed more than once.',
      });
    }
    if (!task.targetLanguages.includes(task.originalLanguage)) {
      ctx.addIssue({
        code: 'custom',
        path: ['targetLanguages'],
        message: 'The target languages must include the original language.',
      });
    }
    for (const language of Object.keys(task.translations) as Language[]) {
      if (!task.targetLanguages.includes(language)) {
        ctx.addIssue({
          code: 'custom',
          path: ['targetLanguages'],
          message: 'The target languages must include every translated language.',
        });
      }
    }

    // T10 (QZ-D153): rejectionReason only on a draft or a rejected task.
    if (task.rejectionReason !== undefined && task.status === 'approved') {
      ctx.addIssue({
        code: 'custom',
        path: ['rejectionReason'],
        message: 'A rejection reason is only allowed on a draft or a rejected task.',
      });
    }

    // T11 (QZ-D115): every translation's choices and items are as many as the original's.
    const original = task.translations[task.originalLanguage];
    if (original) {
      for (const [language, translation] of Object.entries(task.translations) as [
        Language,
        TaskTranslation | undefined,
      ][]) {
        if (!translation || language === task.originalLanguage) {
          continue;
        }

        if (
          original.choices &&
          translation.choices &&
          translation.choices.length !== original.choices.length
        ) {
          ctx.addIssue({
            code: 'custom',
            path: ['translations', language, 'choices'],
            message: 'A translation needs as many choices as the original.',
          });
        }

        if (
          original.items &&
          translation.items &&
          translation.items.length !== original.items.length
        ) {
          ctx.addIssue({
            code: 'custom',
            path: ['translations', language, 'items'],
            message: 'A translation needs as many items as the original.',
          });
        }
      }
    }

    // T12 (QZ-D131): ageGroups has no duplicates.
    if (new Set(task.ageGroups).size !== task.ageGroups.length) {
      ctx.addIssue({
        code: 'custom',
        path: ['ageGroups'],
        message: 'An age group is listed more than once.',
      });
    }
  });
export type Task = z.infer<typeof taskSchema>;

/** QZ-D37: draft → approved | rejected; approved | rejected → draft. */
export const canTransition = (from: TaskStatus, to: TaskStatus): boolean =>
  from === 'draft' ? to !== 'draft' : to === 'draft';

/** The popularity score's smoothing constant (QZ-D153). */
export const POPULARITY_SMOOTHING = 5;

/** The popularity score at or below which a task is withdrawn (QZ-D153). */
export const POPULARITY_WITHDRAW_AT = -0.5;

/**
 * A task's popularity: −1 to 1, 0 for a task with no feedback; derived, never stored (QZ-D153).
 */
export const popularityScore = (task: Pick<Task, 'usedCount' | 'declinedCount'>): number => {
  const used = task.usedCount ?? 0;
  const declined = task.declinedCount ?? 0;
  return (used - declined) / (used + declined + POPULARITY_SMOOTHING);
};

/** Whether a task's popularity score has fallen to or below the withdrawal threshold (QZ-D153). */
export const isUnpopular = (task: Pick<Task, 'usedCount' | 'declinedCount'>): boolean =>
  popularityScore(task) <= POPULARITY_WITHDRAW_AT;

/** The rejection reason a withdrawal for low popularity writes (QZ-D153). */
export const UNPOPULAR_REASON = 'Unpopular';

/** One decline's entry in `declineReasons`, `"<language>: <reason>"` (QZ-D156). */
export const declineReasonEntry = (language: Language, reason: string): string =>
  `${language}: ${reason}`;

/**
 * The rejection reason a declined-feedback withdrawal writes, cut to `REJECTION_REASON_MAX`
 * (QZ-D156).
 */
export const feedbackRejectionReason = ({
  language,
  reason,
  finding,
  suggestedFix,
}: {
  language: Language;
  reason: string;
  finding: string;
  suggestedFix: string | null;
}): string => {
  let result = `Declined (${language}): "${reason}" — ${finding}`;
  if (suggestedFix !== null) {
    result += ` Suggested fix: ${suggestedFix}`;
  }
  return result.slice(0, REJECTION_REASON_MAX);
};

/**
 * The translation record an approval starts (QZ-D113): pending while a target language is
 * missing, done otherwise, with no attempts yet and the cost so far kept. Shared by a manual
 * approval and a job's auto-approval (QZ-D161).
 */
export const approvalTranslation = (
  task: Pick<Task, 'targetLanguages' | 'translations' | 'translation'>
): TaskTranslationState => ({
  status: missingLanguages(task).length > 0 ? 'pending' : 'done',
  attempts: 0,
  microUsd: task.translation?.microUsd ?? 0,
});

/**
 * The target languages in `LANGUAGES` order that have no translation yet (QZ-D130, QZ-D113).
 */
export const missingLanguages = (
  task: Pick<Task, 'targetLanguages' | 'translations'>
): Language[] =>
  LANGUAGES.filter(
    (language) =>
      task.targetLanguages.includes(language) && task.translations[language] === undefined
  );
